/**
 * Map an in-note href to a hub deck stop so editors can link to chapters
 * without leaving the story. Recognizes hashes (`#virginia`, `#loudoun`),
 * `hub-slide-*` ids, and each chapter’s `featureHref` path.
 */

import {
  type DeckSlideId,
  slides,
} from "@story/data-centers-hub/config/slides";

const HASH_ALIASES: Readonly<Record<string, DeckSlideId>> = {
  note: "note",
  intro: "note",
  title: "title",
  virginia: "virginia",
  loudoun: "virginia",
  utah: "utah",
  arizona: "arizona",
  credits: "credits",
};

function pathnameOf(href: string): string | null {
  try {
    if (href.startsWith("#")) {
      return null;
    }
    if (href.startsWith("/")) {
      return href.split(/[?#]/)[0] ?? href;
    }
    const url = new URL(href, "https://www.theverge.com");
    return url.pathname;
  } catch {
    return null;
  }
}

function hashTarget(href: string): string | null {
  const hashIndex = href.indexOf("#");
  if (hashIndex < 0) {
    return null;
  }
  const raw = href.slice(hashIndex + 1).split(/[?&]/)[0];
  if (!raw) {
    return null;
  }
  try {
    return decodeURIComponent(raw).toLowerCase();
  } catch {
    return raw.toLowerCase();
  }
}

/**
 * Returns a deck id when `href` is an in-hub chapter link; otherwise null
 * (leave the browser / article navigation alone).
 */
export function resolveHubSlideLink(href: string): DeckSlideId | null {
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) {
    return null;
  }

  const hash = hashTarget(trimmed);
  if (hash) {
    const fromId = hash.startsWith("hub-slide-")
      ? hash.slice("hub-slide-".length)
      : hash;
    const aliased = HASH_ALIASES[fromId];
    if (aliased) {
      return aliased;
    }
  }

  const path = pathnameOf(trimmed);
  if (!path) {
    return null;
  }
  for (const slide of slides) {
    const feature = slide.featureHref;
    if (!feature) {
      continue;
    }
    if (path === feature || path.endsWith(feature)) {
      return slide.id;
    }
  }
  return null;
}
